import type { AgentMessageToolCalls, UUID } from '@aline/common';
import { Apollo } from 'src/gql/generated';

type AgentMessageStatus = Apollo.AgentMessageStatus;

/**
 * Chat User Message
 */
export type ChatUserMessage = {
  id: string;
  role: Apollo.AgentMessageRole;
  status: AgentMessageStatus;
  text: string;
};

/**
 * Chat Assistant Message Suggestion Status
 */
enum ClientSuggestionStatus {
  DocumentNotInView = 'DOCUMENT_NOT_IN_VIEW',
  NotAlineDoc = 'NOT_ALINE_DOC',
  TextChanged = 'TEXT_CHANGED',
}
export type SuggestionStatus = Apollo.MessageSuggestionStatus | ClientSuggestionStatus;

export const SuggestionStatus = {
  ...Apollo.MessageSuggestionStatus,
  ...ClientSuggestionStatus,
};

export type ChatMessageSuggestionStatus = {
  status: Apollo.MessageSuggestionStatus;
  userId: UUID;
};

/**
 * A tool waiting mid-turn on the system rather than the user. An OCR wait
 * persisted before question runs existed has no `kind`.
 */
export type MidTurnPause =
  | {
      documentId: string;
      fileVersionId: string;
      nodeId: number;
      toolCallId: string;
      toolInput: Record<string, unknown>;
      toolName: 'queryDocument' | 'readDocument';
      kind?: 'documentExtraction';
    }
  | {
      batchId: string;
      kind: 'questionRun';
      nodeId: number;
      toolCallId: string;
      toolInput: Record<string, unknown>;
      toolName: 'documentBaseAnalysisTool' | 'reportAnalysisTool';
      progress?: { done: number; total: number; queuedAt?: string };
    };

/**
 * Durable system waits on assistant message.data, mirroring server
 * MessageData. The banner only cares about phase === 'prePlanning'; mid-turn
 * waits render on their tool-call card.
 */
export type DocumentExtractionPause =
  | { fileVersionIds: string[]; phase: 'prePlanning' }
  | { pauses: MidTurnPause[]; phase: 'midTurn' };

/** The report a question run was saved as, mirroring server `SavedQuestionRunReport`. */
export type SavedQuestionRunReport = { name: string; reportId: UUID };

/**
 * Chat Assistant Message
 */
export type ChatAssistantMessage = {
  createdAt: string;
  data: {
    documentExtractionPause?: DocumentExtractionPause;
    /** Question runs approved this turn, keyed by `batchId`. Only what the client reads. */
    questionRunBatches?: Record<string, { savedReport?: SavedQuestionRunReport }>;
    suggestionStates?: Record<string, ChatMessageSuggestionStatus>;
    toolCalls?: AgentMessageToolCalls;
    uiParts?: unknown[];
  };
  id: string;
  role: Apollo.AgentMessageRole;
  status: AgentMessageStatus;
  text: string;
  feedback?: Apollo.AgentMessageFeedback | null;
  /**
   * Raw planState JSON for harness-authored messages; null for V1. Validated
   * against PlanStateSchema at render time inside the PlanTree component, so
   * we keep the loose Record<string, any> at the GraphQL boundary.
   */
  planState?: Record<string, unknown> | null;
};

/**
 * Combination of User and Assistant Message
 */
export type ChatMessage = ChatUserMessage | ChatAssistantMessage;
